/**
 * Microphone-recording hook — wraps the MediaRecorder API.
 *
 * Lifecycle:
 *   const { recording, error, start, stop, blob } = useVoiceRecorder();
 *   await start();   // requests mic permission + starts capture
 *   // ...user holds-down or otherwise records...
 *   const captured = await stop();   // returns the final Blob
 *
 * Captured chunks are accumulated in a ref so React re-renders during
 * recording stay cheap (we only care about the start/stop boundaries).
 *
 * Browser support: this assumes a Vite dev/prod build. Recording requires
 * a secure context (HTTPS or localhost). The composer disables the mic
 * button + shows a toast when navigator.mediaDevices is missing.
 *
 * Single shared implementation (audit D3) — previously duplicated byte-for-byte
 * across the productive / respondent-chat / interview SPAs. The duplication WAS
 * the ASR bug: one copy lagged behind and re-introduced a timeslice. See the
 * `recorder.start()` comment below.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export interface VoiceRecorderState {
  recording: boolean;
  /** Waiting for microphone permission; prevents concurrent capture starts. */
  starting: boolean;
  error: string | null;
  /** Blob of the most recently completed recording. Null until first stop(). */
  blob: Blob | null;
  /** Wall-clock seconds of the most recent capture. */
  duration_s: number;
}

export interface VoiceRecorderControls {
  start: () => Promise<void>;
  stop: () => Promise<Blob | null>;
  reset: () => void;
}

const PREFERRED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
];

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  for (const mt of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(mt)) return mt;
  }
  return undefined;
}

export function useVoiceRecorder(): VoiceRecorderState & VoiceRecorderControls {
  const [recording, setRecording] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [durationS, setDurationS] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startedAtRef = useRef<number>(0);
  const mountedRef = useRef(true);
  const startingRef = useRef(false);
  const stopPromiseRef = useRef<Promise<Blob | null> | null>(null);

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
  }, []);

  // On unmount, make sure we release the mic.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanupStream();
    };
  }, [cleanupStream]);

  const start = useCallback(async () => {
    if (startingRef.current || mediaRecorderRef.current) return;
    setError(null);
    setBlob(null);
    setDurationS(0);
    chunksRef.current = [];

    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setError("Микрофон недоступен в этом браузере");
      return;
    }

    let stream: MediaStream;
    startingRef.current = true;
    setStarting(true);
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      // DOMException is the spec'd type but jsdom + some WebKit variants
      // raise plain Error with `name` set — match by name to cover both.
      const name = (err as { name?: string } | null)?.name;
      if (mountedRef.current) setError(
        name === "NotAllowedError" || name === "PermissionDeniedError"
          ? "Доступ к микрофону отклонён"
          : "Не удалось получить доступ к микрофону",
      );
      return;
    } finally {
      startingRef.current = false;
      if (mountedRef.current) setStarting(false);
    }

    // Permission dialogs can outlive a route change. Never start capturing
    // after the composer was removed; release the newly granted stream.
    if (!mountedRef.current) {
      for (const track of stream.getTracks()) track.stop();
      return;
    }

    streamRef.current = stream;
    const mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
    } catch (err) {
      cleanupStream();
      setError(err instanceof Error ? err.message : "MediaRecorder не поддерживается");
      return;
    }

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onerror = (event) => {
      const e = (event as Event & { error?: Error }).error;
      setError(e?.message ?? "Ошибка записи");
    };

    mediaRecorderRef.current = recorder;
    startedAtRef.current = Date.now();
    // No timeslice — fire `ondataavailable` ONCE on stop with a single, complete
    // webm (intact EBML header). A timeslice fragments the recording into
    // header-less continuation chunks; reassembled, the container header was
    // sometimes malformed and the ASR's ffmpeg rejected it ("EBML header parsing
    // failed" → 400 → 503 → "Ошибка распознавания", intermittent, recovered only on
    // refresh). Mirrors the interview-SPA fix (commit 9bb88b4).
    try {
      recorder.start();
      setRecording(true);
    } catch (err) {
      cleanupStream();
      setError(err instanceof Error ? err.message : "Ошибка записи");
    }
  }, [cleanupStream]);

  const stop = useCallback(async (): Promise<Blob | null> => {
    if (stopPromiseRef.current) return stopPromiseRef.current;
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      setRecording(false);
      cleanupStream();
      return null;
    }

    const pending = new Promise<Blob | null>((resolve) => {
      recorder.onstop = () => {
        const captured = chunksRef.current;
        const mimeType = recorder.mimeType || "audio/webm";
        const out = captured.length ? new Blob(captured, { type: mimeType }) : null;
        chunksRef.current = [];
        cleanupStream();
        const seconds = Math.max(0, (Date.now() - startedAtRef.current) / 1000);
        setDurationS(seconds);
        setBlob(out);
        setRecording(false);
        resolve(out);
      };
      recorder.stop();
    });
    stopPromiseRef.current = pending;
    try {
      return await pending;
    } finally {
      stopPromiseRef.current = null;
    }
  }, [cleanupStream]);

  const reset = useCallback(() => {
    setError(null);
    setBlob(null);
    setDurationS(0);
    chunksRef.current = [];
  }, []);

  return { recording, starting, error, blob, duration_s: durationS, start, stop, reset };
}
